import { useEffect, useMemo, useRef, useState } from 'react';

import type { DashboardCopy, MapProviderConfig } from '../shared/dashboard-content';
import type { DashboardDataModel } from '../shared/dashboard-data';
import {
  DashboardHeader,
  DashboardMap,
  RegionOverview,
  SelectionSummary,
  StatsCards,
  StatusBar,
  TeamPanel,
  TimelinePanel,
  ZoneDataTabs,
} from './components';
import { DEFAULT_POLYGON_STYLE, panelClassName, SELECTED_POLYGON_STYLE } from './utils';
import type { PolygonLayer } from './utils';

import './styles/index.less';

interface DesktopDashboardProps {
  data: DashboardDataModel;
  copy: DashboardCopy;
  mapConfig: MapProviderConfig;
}

export function DesktopDashboard({ data, copy, mapConfig }: DesktopDashboardProps) {
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<number[]>([]);
  const polygonLayerMapRef = useRef<Map<number, PolygonLayer>>(new Map());

  const teams = data.teams.slice(0, 5);
  const realZone = data.zoneMetrics.find((zone) => !zone.isMock) ?? data.zoneMetrics[data.zoneMetrics.length - 1];

  const selectedAreaM2 = useMemo(() => {
    if (!selectedPolygonIds.length) {
      return 0;
    }

    const selectedSet = new Set(selectedPolygonIds);
    return data.featureCollection.features.reduce((sum, feature) => {
      if (selectedSet.has(feature.properties.OBJECTID)) {
        return sum + feature.properties.Shape_Area;
      }
      return sum;
    }, 0);
  }, [data.featureCollection.features, selectedPolygonIds]);

  const clearSelection = () => {
    setSelectedPolygonIds([]);
    for (const polygonLayer of polygonLayerMapRef.current.values()) {
      polygonLayer.closePopup?.();
    }
  };

  const togglePolygonSelection = (objectId: number) => {
    setSelectedPolygonIds((previous) => {
      if (previous.includes(objectId)) {
        return previous.filter((id) => id !== objectId);
      }
      return [...previous, objectId];
    });
  };

  useEffect(() => {
    const selectedSet = new Set(selectedPolygonIds);
    for (const [objectId, polygonLayer] of polygonLayerMapRef.current) {
      const element = polygonLayer.getElement?.();
      if (!element) {
        continue;
      }

      if (selectedSet.has(objectId)) {
        element.classList.add('is-selected');
        polygonLayer.setStyle?.(SELECTED_POLYGON_STYLE);
      } else {
        element.classList.remove('is-selected');
        polygonLayer.setStyle?.(DEFAULT_POLYGON_STYLE);
      }
    }
  }, [selectedPolygonIds]);

  return (
    <div className="dashboard-root">
      <DashboardMap
        featureCollection={data.featureCollection}
        mapCenter={data.mapCenter}
        tdtToken={mapConfig.tdtToken}
        polygonLayerMapRef={polygonLayerMapRef}
        onPolygonToggle={togglePolygonSelection}
      />

      <DashboardHeader title={copy.title} />

      <main className="dashboard-main">
        <aside className="left-column">
          <StatsCards areaM2={realZone?.areaM2 ?? 0} shapeLengthKm={realZone?.shapeLengthKm ?? 0} />

          <section className={panelClassName()}>
            <h2 className="panel-title">区域概览</h2>
            <RegionOverview zones={data.zoneMetrics} />
          </section>

          <section className={panelClassName('data-panel')}>
            <ZoneDataTabs zones={data.zoneMetrics} />
          </section>

          <TeamPanel teams={teams} />
        </aside>

        <aside className="right-column">
          <TimelinePanel items={data.timeline} />
        </aside>
      </main>

      <SelectionSummary
        selectedCount={selectedPolygonIds.length}
        selectedAreaM2={selectedAreaM2}
        onClear={clearSelection}
      />

      <StatusBar
        locationText={copy.locationText}
        dataUpdateText={copy.dataUpdateText}
        mapProviderText={copy.mapProviderText}
        copyrightText={copy.copyrightText}
      />
    </div>
  );
}
